import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave, fnGetTableById } from "@shared/api/restApi";
import {
  getInitFormData,
  getNoSlip,
  getTablebySql,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import SnResizeBar from "@shared/components/SnResizeBar";
import { dateToString, formatYyyyMMdd } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string;
  [key: string]: any;
}

interface SizeMapValue {
  label: string;
  sort: number;
}

const SN_PRD_WMO_REG_001_TEX = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
    grid4: React.createRef(),
  });
  // 최초 조회에서 받은 롱포맷 피벗 데이터를 캐시한다.
  const pivotLongDataRef = useRef<any[]>([]);
  // cd_var3 넣기위한
  const sizeToSlotRef = useRef<Map<string, string>>(new Map());
  // 고정 슬롯(qt_wo1~qt_wo20)을 유지하고 헤더/노출만 변경한다.
  const sizeSlotColumns = useMemo(
    () => Array.from({ length: 20 }, (_, index) => `qt_wo${index + 1}`),
    []
  );
  const sizeSortMapRef = useRef<Map<string, number>>(new Map());
  const SIZE_SORT_FALLBACK = 9999;
  const sizeCodeCollator = useMemo(
    () =>
      new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    []
  );

  const toFiniteNumber = (value: any, fallback = SIZE_SORT_FALLBACK) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : fallback;
  };

  const getSizeSort = (sizeKey: string, row?: any) => {
    const mapSort = sizeSortMapRef.current.get(sizeKey);
    if (mapSort !== undefined) return mapSort;
    return toFiniteNumber(row?.no_seq_sort);
  };

  const addSizeToMap = (sizeMap: Map<string, SizeMapValue>, row: any) => {
    const sizeKey = `${row.cd_var3 ?? ""}`.trim();
    if (!sizeKey) return;

    const sizeLabel = `${row.nm_var3 ?? sizeKey}`.trim() || sizeKey;
    const sizeSort = getSizeSort(sizeKey, row);
    const current = sizeMap.get(sizeKey);
    if (!current || sizeSort < current.sort) {
      sizeMap.set(sizeKey, { label: sizeLabel, sort: sizeSort });
    }
  };

  const getSortedSizeList = (sizeMap: Map<string, SizeMapValue>) =>
    Array.from(sizeMap, ([key, value]) => ({
      key,
      label: value.label,
      sort: value.sort,
    })).sort((a, b) => {
      if (a.sort !== b.sort) return a.sort - b.sort;
      return sizeCodeCollator.compare(a.key, b.key);
    });

  const getPivotRowsInSourceOrder = (rows: any[]) =>
    rows
      .sort((a, b) => Number(a.__row_order ?? 0) - Number(b.__row_order ?? 0))
      .map(({ __row_order, ...row }) => row);

  const loadSizeSortMap = async () => {
    const rows = await getTablebySql({
      sql: `
        select cd_flag, no_seq_sort
          from snotes.mas_codel
         where cd_firm = :cd_firm
           and cd_clas = 'MAS168'
           and yn_use = 'Y'
         order by coalesce(no_seq_sort, 9999), cd_flag
      `,
      cd_firm: userInfo.cd_firm,
    });
    const nextMap = new Map<string, number>();
    (rows || []).forEach((row: any) => {
      const sizeKey = `${row.cd_flag ?? ""}`.trim();
      if (!sizeKey) return;
      nextMap.set(sizeKey, toFiniteNumber(row.no_seq_sort));
    });
    sizeSortMapRef.current = nextMap;
  };

  const ensureSizeSortMap = async () => {
    if (sizeSortMapRef.current.size > 0) return;
    await loadSizeSortMap();
  };
  const isRestoring = useRef(false);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const isProgrammaticChangeRef = useRef(false);
  const [selectedTabPage, setSelectedTabPage] = useState<string>("1");
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedLineDocu, setSelectedLineDocu] = useState<number>(0);
  const focusKeyRef = useRef<{
    no_wo?: string;
  } | null>(null);
  const _dt = useRef<any>();
  const noLineP = useRef<number>(0);
  const _chk_newRow = useRef<string>("");
  const isYnEnd = useRef<boolean>(true);
  const _nm_rout_old = useRef<string>("");
  const _no_style_old = useRef<string>("");
  const woInfoRef = useRef({ cd_wo: "", st_wo: "" });

  const visible = [
    { cd_column: "qt_wo1", nm_column: "1", fg_visible: "N" },
    { cd_column: "qt_wo2", nm_column: "2", fg_visible: "N" },
    { cd_column: "qt_wo3", nm_column: "3", fg_visible: "N" },
    { cd_column: "qt_wo4", nm_column: "4", fg_visible: "N" },
    { cd_column: "qt_wo5", nm_column: "5", fg_visible: "N" },
    { cd_column: "qt_wo6", nm_column: "6", fg_visible: "N" },
    { cd_column: "qt_wo7", nm_column: "7", fg_visible: "N" },
    { cd_column: "qt_wo8", nm_column: "8", fg_visible: "N" },
    { cd_column: "qt_wo9", nm_column: "9", fg_visible: "N" },
    { cd_column: "qt_wo10", nm_column: "10", fg_visible: "N" },
    { cd_column: "qt_wo11", nm_column: "11", fg_visible: "N" },
    { cd_column: "qt_wo12", nm_column: "12", fg_visible: "N" },
    { cd_column: "qt_wo13", nm_column: "13", fg_visible: "N" },
    { cd_column: "qt_wo14", nm_column: "14", fg_visible: "N" },
    { cd_column: "qt_wo15", nm_column: "15", fg_visible: "N" },
    { cd_column: "qt_wo16", nm_column: "16", fg_visible: "N" },
    { cd_column: "qt_wo17", nm_column: "17", fg_visible: "N" },
    { cd_column: "qt_wo18", nm_column: "18", fg_visible: "N" },
    { cd_column: "qt_wo19", nm_column: "19", fg_visible: "N" },
    { cd_column: "qt_wo20", nm_column: "20", fg_visible: "N" },
  ];

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        const popup_apply = [
          { target: "cd_wh_rcv", source: "cd_wh" },
          { target: "nm_wh", source: "nm_wh" },
        ];
        const grid1 = rtn.grid.grid1.map((col: any) => {
          let newCol = { ...col };
          if (col.cd_column === "cd_wh_rcv") {
            newCol.popup_apply = popup_apply;
          }
          return newCol;
        });
        setColums({
          grid1: grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sqlQuery = {
          sql: "select cd_wo, case when coalesce(yn_autorel,'N') = 'Y' then 'R' else 'F' end as st_wo from mas_wotype where cd_firm = :cd_firm and yn_default = 'Y' limit 1",
          cd_firm: userInfo.cd_firm,
        };
        const result = await getTablebySql(sqlQuery);
        if (result.length > 0) {
          woInfoRef.current = {
            cd_wo: result[0].cd_wo,
            st_wo: result[0].st_wo,
          };
        }
      } catch (error) {}
    };
    fetchData();
  }, [userInfo]);

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    const modifiedRowsL2 = gridRefs.current["grid3"].current.getChange();
    const modifiedRowsL3 = gridRefs.current["grid4"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0 ||
      modifiedRowsL2.modifiedRowcount > 0 ||
      modifiedRowsL3.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_prd_wmo_reg_001_tex_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      gridRefs.current["grid4"].current.resetUpdate();
      setSelectedDocu("");
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
        grid4: data[3] || [],
      });
      // 포커스 변경 시 재조회하지 않도록 롱포맷 데이터를 메모리에 보관.
      pivotLongDataRef.current = data[1] || [];
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const searchForm = searchRef.current.getFormdata();
      if (searchForm.tp_wo === "") {
        await showDialog("alter", "공정경로유형을 선택하시기 바랍니다.");
        return;
      }

      const sqlQuery1 = {
        sql: `select pr.cd_wo,pr.cd_rout,pr.nm_rout,mw.nm_wo
        from prd_rout pr
        left outer join mas_wotype mw on pr.cd_firm = mw.cd_firm and pr.cd_wo = mw.cd_wo
        where pr.cd_firm = :cd_firm and cd_rout = :cd_rout`,
        cd_firm: userInfo.cd_firm,
        cd_rout: "T20",
      };
      const data = await getTablebySql(sqlQuery1);

      const salSohChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (salSohChanges.length > 0) return;

      const today = dateToString(new Date());
      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        no_wo: "",
        cd_wo: data[0].cd_wo,
        cd_rout: data[0].cd_rout,
        nm_rout: data[0].nm_rout,
        nm_wo: data[0].nm_wo,
        cd_user_reg: userInfo.cd_user,
        cd_gp: userInfo.cd_gp,
        cd_emp: userInfo.cd_emp,
        dt_wo: today,
        dt_start: today,
        dt_end: today,
        qt_wo: 0,
        fg_wo: "ETC",
        tp_rout: searchForm.tp_wo,
        tp_wo: searchForm.tp_wo,
        qt_work: 0,
        qt_ref: 0,
        no_line_so: 0,
      };
      gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onAddRow = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        await showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return;

      if (selectedTabPage === "2") {
        noLineP.current =
          gridRefs.current["grid3"].current.getDisplayedRowCount();
        _chk_newRow.current = "M";
        const selectedRows =
          gridRefs.current["grid1"].current.getSelectedRows();
        const maxNoLine =
          gridRefs.current["grid3"].current.getMaxValue("no_line");
        const today = dateToString(new Date());
        let newRow: any;
        switch (_chk_newRow.current) {
          case "R":
            newRow = {
              cd_firm: userInfo.cd_firm,
              cd_user_reg: userInfo.cd_user,
              no_wo: selectedRows[0]?.no_wo,
              dt_wo: today,
              no_line: _dt.current[0][noLineP.current].no_seq,
              cd_op: _dt.current[0][noLineP.current].cd_op,
              nm_op: _dt.current[0][noLineP.current].nm_op,
              qt_wo: selectedRows[0]?.qt_wo,
              yn_end: _dt.current[0][noLineP.current].yn_end,
              yn_work: _dt.current[0][noLineP.current].yn_work,
              cd_cust: _dt.current[0][noLineP.current].cd_cust_sct,
              nm_cust_sort: _dt.current[0][noLineP.current].nm_cust_sort,
              st_proc: selectedRows[0]?.st_wo,
              qt_work: 0,
            };
            noLineP.current++;
            _chk_newRow.current = "M";
            break;
          case "M":
            newRow = {
              cd_firm: userInfo.cd_firm,
              cd_user_reg: userInfo.cd_user,
              no_line: maxNoLine + 1,
              dt_wo: today,
              qt_wo: selectedRows[0]?.qt_wo,
              no_wo: selectedRows[0]?.no_wo,
              st_proc: selectedRows[0]?.st_wo,
              yn_end: "N",
              yn_work: "N",
              qt_work: 0,
            };
            break;
        }
        gridRefs.current["grid3"].current?.addRow(newRow);
      } else if (selectedTabPage === "3") {
        noLineP.current =
          gridRefs.current["grid4"].current.getDisplayedRowCount();
        _chk_newRow.current = "M";
        const selectedRows =
          gridRefs.current["grid1"].current.getSelectedRows();
        const maxNoLine =
          gridRefs.current["grid4"].current.getMaxValue("no_line");

        let newRow: any;
        switch (_chk_newRow.current) {
          case "R":
            const qtUnit = Number(_dt.current[0][noLineP.current].qt_need);
            newRow = {
              cd_firm: userInfo.cd_firm,
              cd_user_reg: userInfo.cd_user,
              no_line: maxNoLine + 1,
              no_wo: selectedRows[0]?.no_wo,
              cd_matl: _dt.current[0][noLineP.current].cd_matl,
              nm_matl: _dt.current[0][noLineP.current].nm_matl,
              dc_item_unit: _dt.current[0][noLineP.current].dc_item_unit,
              qt_need:
                Number(_dt.current[0][noLineP.current].qt_need) *
                Number(selectedRows[0]?.qt_wo),
              qt_unit: qtUnit,
              cd_op: _dt.current[0][noLineP.current].cd_op,
            };
            noLineP.current++;
            _chk_newRow.current = "M";
            break;
          case "M":
            newRow = {
              cd_firm: userInfo.cd_firm,
              cd_user_reg: userInfo.cd_user,
              no_wo: selectedRows[0]?.no_wo,
              no_line: maxNoLine + 1,
            };
            break;
        }
        gridRefs.current["grid4"].current?.addRow(newRow);
      } else {
        const selectedRows =
          gridRefs.current["grid1"].current.getSelectedRows();
        const maxNoLine =
          gridRefs.current["grid2"].current.getMaxValue("no_line");

        let newRow: any;
        newRow = {
          cd_firm: userInfo.cd_firm,
          no_wo: selectedRows[0]?.no_wo,
          cd_item: "",
          no_line: maxNoLine + 1,
        };
        gridRefs.current["grid2"].current?.addRow(newRow);
      }
    } catch (error: any) {}
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const selectedRows = gridRefs.current["grid1"].current.getSelectedRows();
    if (selectedRows[0]?.st_wo === "S") {
      await showDialog("error", "진행 중인 작업지시는 삭제 할 수 없습니다.");
      return;
    }
    if (selectedRows[0]?.st_wo === "C") {
      await showDialog("error", "마감 된 작업지시는 삭제 할 수 없습니다.");
      return;
    }
    if (selectedRows[0]?.qt_work !== 0) {
      await showDialog("error", "진행된 수량이 존재합니다.");
      return;
    }

    const footerData3 = gridRefs.current["grid3"].current.getFooterData();
    const qtWork = footerData3?.qt_work;
    if (qtWork !== 0) {
      await showDialog("error", "공정 진행된 수량이 존재합니다.");
      return;
    }
    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;
    gridRefs.current["grid4"].current?.deleteAllRows();
    gridRefs.current["grid3"].current?.deleteAllRows();
    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();
    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      gridRefs.current["grid4"].current.resetUpdate();
      return;
    }

    await onSave();
    afterDelete();
  };

  function afterDelete() {
    if (gridRefs.current["grid1"].current.getDisplayedRowCount() === 0) {
      gridRefs.current["grid2"].current.changeColumnConfigs(visible);
    }
  }

  const onDeleteRow = async () => {
    if (
      gridRefs.current["grid3"].current?.getDisplayedRowCount() < 0 ||
      gridRefs.current["grid4"].current?.getDisplayedRowCount() < 0
    )
      return;

    const selectedRows = gridRefs.current["grid1"].current.getSelectedRows();
    const st_wo = selectedRows[0].st_wo;
    const footerData4 = gridRefs.current["grid4"].current.getFooterData();
    const qt_work = footerData4?.qt_work;
    if (selectedRows[0]?.no_wo !== "") {
      if (st_wo === "C") {
        await showDialog(
          "error",
          "지시상태 마감의 경우 행추가 하실 수 없습니다."
        );
        return;
      } else if (st_wo === "R") {
        if (qt_work > 0) {
          await showDialog(
            "error",
            "발행된 작업지시므로 행추가/행삭제 할 수 없습니다."
          );
          return;
        }
      } else if (st_wo === "S") {
        await showDialog(
          "error",
          "진행 중인 작업지시므로 행추가/행삭제 할 수 없습니다."
        );
        return;
      }
    }

    if (selectedTabPage === "2") {
      gridRefs.current["grid3"].current?.deleteSelectedRows();
    } else if (selectedTabPage === "3") {
      gridRefs.current["grid4"].current?.deleteSelectedRows();
    } else {
      gridRefs.current["grid2"].current?.deleteSelectedRows();
    }
  };

  const onSave = async () => {
    let prdWoTexChanges;
    let prdWoproctexChg;
    let prdWoprocChanges;
    let prdVariantChanges;

    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();
      gridRefs.current["grid3"].current.stopEditing();
      gridRefs.current["grid4"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid3"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      prdWoTexChanges = gridRefs.current["grid1"].current.getChange();
      const hasInsertOrUpdate =
        prdWoTexChanges.insert.length > 0 || prdWoTexChanges.update.length > 0;

      // ⭐ INSERT / UPDATE 있을 때만 검증
      if (hasInsertOrUpdate) {
        if (!(await validateDetailGrids())) return false;
      }
      const headerInserts = prdWoTexChanges.insert ?? [];
      prdWoproctexChg = gridRefs.current["grid3"].current.getChange();
      prdWoprocChanges = gridRefs.current["grid4"].current.getChange();
      prdVariantChanges = gridRefs.current["grid2"].current.getChange();
      const variantChanges = prdVariantChanges;
      const pivotRows = [
        ...(variantChanges.insert ?? []),
        ...(variantChanges.update ?? []),
      ];
      const slotToSize = new Map<string, string>();
      sizeToSlotRef.current.forEach((slot, sizeKey) => {
        slotToSize.set(slot, sizeKey);
      });

      const beforeMap = new Map<string, any>();
      pivotLongDataRef.current.forEach((row: any) => {
        const key = `${row.cd_firm}|${row.no_wo}|${row.no_line_wo}|${row.no_line_tex}`;
        beforeMap.set(key, row);
      });
      const insertRows: any[] = [];
      const updateRows: any[] = [];

      let noLineTexSeq = 1;

      pivotRows.forEach((row: any) => {
        sizeSlotColumns.forEach((slot) => {
          const qt = Number(row[slot] ?? 0);
          const cd_var3 = slotToSize.get(slot);
          if (!cd_var3) return;
          // 기존 long row 찾기
          const before = pivotLongDataRef.current.find(
            (r: any) =>
              r.no_wo === row.no_wo &&
              r.no_style === row.no_style &&
              r.cd_var1 === row.cd_var1 &&
              r.cd_var2 === row.cd_var2 &&
              r.cd_var3 === cd_var3
          );
          if (!before) {
            insertRows.push({
              cd_var3,
              qt,
              no_style: row.no_style,
              cd_dummy01: row.cd_dummy01,
              cd_item: row.cd_item,
              cd_var1: row.cd_var1,
              cd_var2: row.cd_var2,
              no_wo: row.no_wo,
              cd_firm: userInfo.cd_firm,
              no_line_wo: 0,
              dt_wo: headerInserts[0].dt_wo,
              no_line_tex: noLineTexSeq++,
              cd_op: "",
              cd_user_reg: userInfo.cd_user,
            });
          }
          if (!before) return;
          if (before.qt !== qt) {
            updateRows.push({
              ...before,
              qt,
              cd_user_upd: userInfo.cd_user,
            });
            return;
          }
        });
      });
      //delete 로직
      const pivotDeleteRows = prdVariantChanges.delete ?? [];
      const deleteRows: any[] = [];
      pivotDeleteRows.forEach((pivotRow: any) => {
        pivotLongDataRef.current.forEach((longRow: any) => {
          if (
            longRow.no_wo === pivotRow.no_wo &&
            longRow.no_style === pivotRow.no_style &&
            longRow.cd_var1 === pivotRow.cd_var1 &&
            longRow.cd_var2 === pivotRow.cd_var2 &&
            longRow.cd_dummy01 === pivotRow.cd_dummy01
          ) {
            deleteRows.push({
              cd_firm: longRow.cd_firm,
              no_wo: longRow.no_wo,
              no_line_tex: longRow.no_line_tex,
              cd_user_upd: userInfo.cd_user,
            });
          }
        });
      });
      const newPrdVariantChanges = {
        insert: insertRows,
        update: updateRows,
        delete: deleteRows,
        modifiedRowcount:
          insertRows.length + updateRows.length + deleteRows.length,
      };
      if (
        prdWoTexChanges.modifiedRowcount < 1 &&
        prdWoproctexChg.modifiedRowcount < 1 &&
        prdWoprocChanges.modifiedRowcount < 1 &&
        newPrdVariantChanges.modifiedRowcount < 1
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const allChanges = [
        ...(prdVariantChanges.insert ?? []),
        ...(prdVariantChanges.update ?? []),
      ];
      for (let i = 0; i < allChanges.length; i++) {
        const row = allChanges[i];
        if (!row) continue;

        const cd_item =
          String(row.no_style ?? "") +
          String(row.cd_var1 ?? "") +
          String(row.cd_var2 ?? "");

        const sqlQuery = {
          sql: `select count(*) as cnt from mas_item where cd_firm = :cd_firm and cd_item = :cd_item`,
          cd_firm: userInfo.cd_firm,
          cd_item: cd_item,
        };

        try {
          const result = await getTablebySql(sqlQuery);
          const cnt =
            (result &&
              result[0] &&
              (result[0].cnt ?? result[0].COUNT ?? result[0]["count(*)"])) ??
            0;
          if (cnt < 1) {
            await showDialog(
              "alter",
              "품목코드가 존재하지 않습니다 (스타일+FIT+COLOR) "
            );
            return false;
          }
          // cnt 검사해서 insert/skip/alert 등 처리
        } catch (err) {
          return false;
        }
      }
      const lineInserts = prdWoproctexChg.insert ?? [];
      const sublineInserts = prdWoprocChanges.insert ?? [];
      const variant = insertRows;

      for (let row of headerInserts) {
        if (!row.no_wo) {
          const cd_module: any = Menuprops.cd_module;
          const cd_menu: any = Menuprops.cd_menu;
          try {
            const no_slip = await getNoSlip(
              cd_module,
              1,
              "WMO",
              row.dt_wo,
              cd_menu
            );
            if (no_slip) {
              row.no_wo = no_slip;

              const focusedNodeH =
                gridRefs.current["grid1"].current.focusedRowNode();
              focusedNodeH.setDataValue("no_wo", no_slip);

              for (let row of lineInserts) {
                if (!row.no_wo) {
                  if (no_slip) {
                    row.no_wo = no_slip;
                  }
                }
              }

              for (let row of sublineInserts) {
                if (!row.no_wo) {
                  if (no_slip) {
                    row.no_wo = no_slip;
                  }
                }
              }

              for (let row of variant) {
                if (!row.no_wo) {
                  if (no_slip) {
                    row.no_wo = no_slip;
                  }
                }
              }
              gridRefs.current["grid2"].current.applyFilters({
                no_wo: no_slip,
              });
              gridRefs.current["grid3"].current.applyFilters({
                no_wo: no_slip,
              });
              gridRefs.current["grid4"].current.applyFilters({
                no_wo: no_slip,
              });
            }
          } catch (error: any) {}
        } else {
          const payload = {
            id: "duplication_wo_tex",
            params: { cd_firm: userInfo.cd_firm, no_wo: row.no_wo },
          };
          const noWo_duplication = await fnGetTableById(payload);
          if (noWo_duplication.length > 0) {
            const error = await showDialog(
              "error",
              "작업지시번호가 중복됩니다."
            );
            if (!error) return;
          }
        }
        if (row.no_po !== "" && row.no_po !== undefined) {
          const payload = {
            id: "duplication_wo_tex",
            params: {
              cd_firm: userInfo.cd_firm,
              no_po: row.no_po,
              tp_wo: row.tp_wo,
            },
          };
          const noPo_duplication = await fnGetTableById(payload);
          if (noPo_duplication.length > 0) {
            const confirm = await showDialog(
              "confirm",
              "지시일 " +
                formatYyyyMMdd(noPo_duplication[0].dt_wo) +
                " 기준,\n중복된 PO번호가 존재합니다.\n저장하시겠습니까?"
            );
            if (!confirm) return;
          }
        }
      }

      const saveData = {
        prd_wo_tex: prdWoTexChanges,
        prd_woproc_tex: prdWoproctexChg,
        prd_wobill_tex: prdWoprocChanges,
        prd_wovariant_tex: newPrdVariantChanges,
      };
      // return;
      const ok = await fnSave("SN_PRD_WMO_REG_001_TEX_SAVE_01", saveData);
      if (!ok) return false;
      const focusedRow = gridRefs.current["grid1"].current.focusedRowNode();

      if (focusedRow) {
        focusKeyRef.current = {
          no_wo: focusedRow.data.no_wo,
        };
      }
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      gridRefs.current["grid4"].current.resetUpdate();
      await onSearch();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid3"].current.resetUpdate(true);
      gridRefs.current["grid4"].current.resetUpdate(true);
      return false;
    }
  };

  const onBtnsClick = async (id: string) => {
    if (id === "addRow") {
      onAddRow();
    }
    if (id === "deleteRow") {
      onDeleteRow();
    }
    if (id === "btnReload") {
      gridRefs.current["grid1"].current.stopEditing();
      const selectedRow =
        gridRefs.current["grid1"].current?.getSelectedRows()[0];
      const focusedRow = gridRefs.current["grid1"].current?.focusedRowNode();
      const payload = {
        id: "duplication_wo_tex",
        params: { cd_firm: userInfo.cd_firm, no_wo: selectedRow.no_wo },
      };
      const noWo_duplication = await fnGetTableById(payload);
      if (noWo_duplication.length > 0) {
        focusedRow.setDataValue("no_style", "");
        focusedRow.setDataValue("nm_style", "");
        focusedRow.setDataValue("no_wo", "");
        await showDialog(
          "warning",
          "중복된 작업지시번호입니다.\n 작업지시번호를 다시 입력 후 품목을 선택해 주세요"
        );
        return;
      }
      if (selectedTabPage === "2") {
        if (selectedRow.qt_wo === 0) {
          await showDialog("alter", "지시량정보가 누락 되었습니다.");
          return;
        }
        if (!gridRefs.current["grid1"].current.requiredValuesCheck()) return;

        const param = {
          cd_firm: userInfo.cd_firm,
          cd_rout: selectedRow.cd_rout,
        };
        const params = {
          id: "sn_prd_wmo_reg_001_tex_grid2",
          params: param,
        };
        _dt.current = await fnGetDataSet(params);
        if (!_dt.current[0]) return;

        if (gridRefs.current["grid3"].current.getDisplayedRowCount() !== 0) {
          const confirm = await showDialog(
            "confirm",
            "생성된 상태에서 전개버튼을 다시 누르면 기존 데이터를 삭제하고 \n다시생성하시겠습니까?"
          );
          if (!confirm) return;

          const grid3Count: any[] = [];
          gridRefs.current["grid3"].current.forEachFilteredNode(
            (node: RowNode) => {
              grid3Count.push(node);
            }
          );
          const gridRowData = grid3Count.map((node: any) => node.data);
          const sourceRows = _dt.current[0];
          const gridRows = gridRowData;

          if (_dt.current[0].length === gridRowData.length) {
            const sortFn = (a: any, b: any) =>
              JSON.stringify(a).localeCompare(JSON.stringify(b));

            const sortedSource = [...sourceRows].sort(sortFn);
            const sortedGrid = [...gridRows].sort(sortFn);

            const isSame = sortedSource.every(
              (row, index) => row === sortedGrid[index]
            );

            const footerData =
              gridRefs.current["grid3"].current.getFooterData();
            if (isSame && footerData.qt_work != 0) {
              await showDialog(
                "error",
                "이미 진행된 데이터가 존재해서 재전개 할수 없습니다."
              );
              return;
            }
          }
        }
        gridRefs.current["grid3"].current?.deleteAllRows();
        let maxNoLine =
          gridRefs.current["grid3"].current.getMaxValue("no_line");
        noLineP.current = 0;
        for (let i = 0; i < _dt.current[0].length; i++) {
          _chk_newRow.current = "R";
          const selectedRows =
            gridRefs.current["grid1"].current.getSelectedRows();
          const today = dateToString(new Date());
          let newRow: any;
          switch (_chk_newRow.current) {
            case "R":
              newRow = {
                cd_firm: userInfo.cd_firm,
                cd_user_reg: userInfo.cd_user,
                no_wo: selectedRows[0]?.no_wo,
                dc_unit: "EA",
                dt_wo: today,
                no_line: _dt.current[0][noLineP.current].no_seq,
                cd_op: _dt.current[0][noLineP.current].cd_op,
                nm_op: _dt.current[0][noLineP.current].nm_op,
                qt_wo: selectedRows[0]?.qt_wo,
                yn_end: _dt.current[0][noLineP.current].yn_end,
                yn_work: _dt.current[0][noLineP.current].yn_work,
                cd_cust: _dt.current[0][noLineP.current].cd_cust_sct,
                nm_cust_sort: _dt.current[0][noLineP.current].nm_cust_sort,
                st_proc: selectedRows[0]?.st_wo,
                qt_work: 0,
              };
              noLineP.current++;
              maxNoLine++;
              _chk_newRow.current = "M";
              break;
            case "M":
              newRow = {
                cd_firm: userInfo.cd_firm,
                cd_user_reg: userInfo.cd_user,
                no_line: maxNoLine + 1,
                dc_unit: "EA",
                dt_wo: today,
                qt_wo: selectedRows[0]?.qt_wo,
                no_wo: selectedRows[0]?.no_wo,
                st_proc: selectedRows[0]?.st_wo,
                yn_end: "N",
                yn_work: "N",
                qt_work: 0,
              };
              maxNoLine++;
              break;
          }
          gridRefs.current["grid3"].current?.addRow(newRow);
          gridRefs.current["grid3"].current.applyFilters({
            no_wo: selectedRows[0]?.no_wo,
          });
        }
      } else if (selectedTabPage === "3") {
        if (selectedRow.qt_wo === 0) {
          showDialog("alter", "지시량정보가 누락 되었습니다.");
          return;
        }
        if (!gridRefs.current["grid1"].current.requiredValuesCheck()) {
          return;
        }

        if (gridRefs.current["grid4"].current.getDisplayedRowCount() !== 0) {
          const confirm = await showDialog(
            "confirm",
            "기존 자재정보를 삭제하고 다시 생성합니다. \n전개 하시겠습니까?"
          );
          if (!confirm) return;
        }

        gridRefs.current["grid4"].current?.deleteAllRows();
        const param = {
          cd_firm: userInfo.cd_firm,
          no_style: selectedRow.no_style,
          length: selectedRow.no_style.toString().length,
          cd_rout: selectedRow.cd_rout,
        };
        noLineP.current = 0;
        const searchParam = {
          id: "sn_prd_wmo_reg_001_tex_grid3",
          params: param,
        };
        _dt.current = await fnGetDataSet(searchParam);
        if (_dt.current[0].length > 0) {
          let maxNoLine =
            gridRefs.current["grid4"].current.getMaxValue("no_line");
          const selectedRows =
            gridRefs.current["grid1"].current.getSelectedRows();

          const newRow: any[] = [];
          for (let i = 0; i < _dt.current[0].length; i++) {
            if (_dt.current[0][noLineP.current].tp_item_c === "FAB") {
              const grid2RowCount =
                gridRefs.current["grid2"].current.getDisplayedRowCount();
              if (grid2RowCount > 0) {
                const groupMap = new Map<string, number>();
                gridRefs.current["grid2"].current.forEachFilteredNode(
                  (node: RowNode) => {
                    const key = node.data.cd_var2;
                    const qt = Number(node.data.qt ?? 0);
                    groupMap.set(key, (groupMap.get(key) || 0) + qt);
                  }
                );
                groupMap.forEach((totalQt, key) => {
                  newRow.push({
                    cd_firm: userInfo.cd_firm,
                    cd_user_reg: userInfo.cd_user,
                    no_line: maxNoLine++,
                    no_wo: selectedRows[0]?.no_wo,
                    cd_matl: _dt.current[0][noLineP.current].cd_matl,
                    nm_matl: _dt.current[0][noLineP.current].nm_matl,
                    dc_item_unit: _dt.current[0][noLineP.current].dc_item_unit,
                    qt_need:
                      totalQt * Number(_dt.current[0][noLineP.current].qt_need),
                    qt_unit: _dt.current[0][noLineP.current].qt_need,
                    cd_op: _dt.current[0][noLineP.current].cd_op,
                    cd_wh: selectedRows[0].cd_wh_rcv,
                    cd_var2: key,
                  });
                });
              }
              noLineP.current++;
            } else {
              _chk_newRow.current = "R";

              switch (_chk_newRow.current) {
                case "R":
                  const qtUnit = Number(
                    _dt.current[0][noLineP.current].qt_need
                  );
                  newRow.push({
                    cd_firm: userInfo.cd_firm,
                    cd_user_reg: userInfo.cd_user,
                    no_line: maxNoLine + 1,
                    no_wo: selectedRows[0]?.no_wo,
                    cd_matl: _dt.current[0][noLineP.current].cd_matl,
                    nm_matl: _dt.current[0][noLineP.current].nm_matl,
                    dc_item_unit: _dt.current[0][noLineP.current].dc_item_unit,
                    qt_need:
                      Number(_dt.current[0][noLineP.current].qt_need) *
                      Number(selectedRows[0]?.qt_wo),
                    qt_unit: qtUnit,
                    cd_op: _dt.current[0][noLineP.current].cd_op,
                    cd_wh: selectedRows[0].cd_wh_rcv,
                  });
                  noLineP.current++;
                  maxNoLine++;
                  _chk_newRow.current = "M";
                  break;
                case "M":
                  newRow.push({
                    cd_firm: userInfo.cd_firm,
                    cd_user_reg: userInfo.cd_user,
                    no_wo: selectedRows[0]?.no_wo,
                    no_line: maxNoLine + 1,
                    cd_wh: selectedRows[0].cd_wh_rcv,
                  });
                  maxNoLine++;
                  break;
              }
            }
          }
          newRow.forEach((row: any) => {
            gridRefs.current["grid4"].current?.addRow(row);
          });
          gridRefs.current["grid4"].current.applyFilters({
            no_wo: selectedRows[0]?.no_wo,
          });
        } else {
          await showDialog("alert", "제품BOM에 해당 스타일 정보가 없습니다.");
          return;
        }
      } else {
        if (!gridRefs.current["grid1"].current.requiredValuesCheck()) {
          return;
        }
        const virtual: any[] = [];
        if (gridRefs.current["grid2"].current.getDisplayedRowCount() !== 0) {
          const confirm = await showDialog(
            "confirm",
            "생성된 상태에서 전개버튼을 다시 누르면 기존 데이터를 삭제하고 \n다시생성하시겠습니까?"
          );
          if (!confirm) return;
        }

        gridRefs.current["grid2"].current?.deleteAllRows();
        const params = {
          id: "sn_prd_wmo_reg_001_tex_reload",
          params: {
            cd_firm: userInfo.cd_firm,
            no_style: selectedRow.no_style,
            no_po: selectedRow.no_po,
            rt_qt_wo: selectedRow.rt_qt_wo,
          },
        };
        const data: any = await fnGetDataSet(params);
        if (data?.[0]?.length > 0) {
          focusedRow.setDataValue("no_so", data[0][0].no_so, "noEvent");
        }
        await ensureSizeSortMap();
        // 선택된 수주(no_so) 기준으로 캐시 데이터를 필터링.
        pivotLongDataRef.current = data[0] || [];
        const filteredLongRows = pivotLongDataRef.current.filter(
          (row: any) => row.no_so === data[0][0].no_so
        );

        // 사이즈 목록 생성(cd_var3 -> 헤더 라벨).
        const sizeMap = new Map<string, SizeMapValue>();
        filteredLongRows.forEach((row: any) => {
          addSizeToMap(sizeMap, row);
        });
        const sizeList = getSortedSizeList(sizeMap);
        // 사이즈를 고정 슬롯(qt_wo1~qt_wo20)에 순서대로 매핑.
        const sizeToSlot = new Map<string, string>();
        sizeList.slice(0, sizeSlotColumns.length).forEach((size, index) => {
          sizeToSlot.set(size.key, sizeSlotColumns[index]);
        });

        const slotLabelByColumn = new Map<string, string>();
        sizeList.slice(0, sizeSlotColumns.length).forEach((size, index) => {
          slotLabelByColumn.set(sizeSlotColumns[index], size.label);
        });
        // (style, fit, color, location) 기준으로 피벗 row 구성 및 합산.
        const groupedRows = new Map<string, any>();
        filteredLongRows.forEach((row: any, index: number) => {
          const rowKey = [
            row.no_style ?? "",
            row.cd_var1 ?? "",
            row.cd_var2 ?? "",
            row.cd_dummy01 ?? "",
            row.cd_item ?? "",
          ].join("|");
          let pivotRow = groupedRows.get(rowKey);
          if (!pivotRow) {
            pivotRow = {
              no_style: row.no_style,
              no_so: row.no_so,
              cd_var1: row.cd_var1,
              cd_var2: row.cd_var2,
              cd_dummy01: row.cd_dummy01,
              cd_item: row.cd_item,
              qt_sum: 0,
              __row_order: index,
            };
            sizeSlotColumns.forEach((slot) => {
              pivotRow[slot] = 0;
            });
            groupedRows.set(rowKey, pivotRow);
          }
          const slot = sizeToSlot.get(`${row.cd_var3 ?? ""}`.trim());
          if (slot) {
            pivotRow[slot] = Number(pivotRow[slot] ?? 0) + Number(row.qt ?? 0);
            pivotRow.qt_sum += Number(row.qt ?? 0);
          }
        });
        columns.grid2.map((row: any) => {
          const colIndex = parseInt(row.nm_column);
          //will use visible false columns'name as index by substracting 1
          //if index of searched data has value, will make column visible and add nm_column

          const dataItem = data[1][colIndex - 1];

          if (dataItem) {
            if (row.cd_column && sizeSlotColumns.includes(row.cd_column)) {
              const slotLabel = slotLabelByColumn.get(row.cd_column);
              const data = {
                cd_column: row.cd_column,
                nm_column: slotLabel ?? row.nm_column,
                fg_visible: slotLabel ? "Y" : "N",
              };
              virtual.push(data);
            }
          }
        });
        setTimeout(() => {
          gridRefs.current["grid2"].current.changeColumnConfigs(virtual);
        }, 0);

        let maxNoLine =
          gridRefs.current["grid2"].current.getMaxValue("no_line");
        const pivotRows = getPivotRowsInSourceOrder(
          Array.from(groupedRows.values())
        );
        const selectedRows =
          gridRefs.current["grid1"].current.getSelectedRows();
        pivotRows.forEach((row: any) => {
          let newRow: any;
          newRow = {
            cd_firm: userInfo.cd_firm,
            no_wo: selectedRows[0]?.no_wo,
            cd_item: row.cd_item,
            no_line: maxNoLine + 1,
            cd_dummy01: row.cd_dummy01,
            cd_var1: row.cd_var1,
            cd_var2: row.cd_var2,
            nm_var1: row.nm_var1,
            nm_var2: row.nm_var2,
            no_style: row.no_style,
            qt: row.qt_sum,
          };
          maxNoLine++;
          sizeSlotColumns.forEach((slot) => {
            newRow[slot] = row[slot] ?? 0;
          });
          gridRefs.current["grid2"].current?.addRow(newRow);
        });
        const focusedNodelH =
          gridRefs.current["grid1"].current.focusedRowNode();
        setTimeout(() => {
          const footerData = gridRefs.current["grid2"].current.getFooterData();
          focusedNodelH.setDataValue("qt_wo", footerData.qt);
        }, 0);
        gridRefs.current["grid2"].current.applyFilters({
          no_wo: selectedRows[0]?.no_wo,
        });
        gridRefs.current["grid2"].current.selectAndFocusRow(0);
        // cd_var3 매핑
        sizeToSlotRef.current = sizeToSlot;
      }
    }
  };

  const onFocusedRowChanged = async (event: any) => {
    const selectedRow = event;
    const virtual: any[] = [];
    // gridRefs.current["grid2"].current.changeColumnConfigs(visible);
    await ensureSizeSortMap();

    // 선택된 수주(no_so)만 뽑아서 피벗 대상 데이터를 최소화한다.
    const filteredLongRows = pivotLongDataRef.current.filter(
      (row: any) => row.no_wo === selectedRow.no_wo
    );
    // 사이즈 목록 생성(cd_var3 -> 헤더 라벨).
    const sizeMap = new Map<string, SizeMapValue>();
    filteredLongRows.forEach((row: any) => {
      addSizeToMap(sizeMap, row);
    });
    const sizeList = getSortedSizeList(sizeMap);
    // 사이즈를 고정 슬롯(qt_wo1~qt_wo20)에 순서대로 매핑.
    const sizeToSlot = new Map<string, string>();
    sizeList.slice(0, sizeSlotColumns.length).forEach((size, index) => {
      sizeToSlot.set(size.key, sizeSlotColumns[index]);
    });
    const slotLabelByColumn = new Map<string, string>();
    sizeList.slice(0, sizeSlotColumns.length).forEach((size, index) => {
      slotLabelByColumn.set(sizeSlotColumns[index], size.label);
    });
    // (style, fit, color, location) 기준으로 피벗 row 구성 및 합산.
    const groupedRows = new Map<string, any>();
    filteredLongRows.forEach((row: any, index: number) => {
      const rowKey = [
        row.no_style ?? "",
        row.cd_var1 ?? "",
        row.cd_var2 ?? "",
        row.cd_dummy01 ?? "",
        row.cd_item ?? "",
      ].join("|");
      let pivotRow = groupedRows.get(rowKey);
      if (!pivotRow) {
        pivotRow = {
          no_style: row.no_style,
          no_wo: row.no_wo,
          cd_var1: row.cd_var1,
          cd_var2: row.cd_var2,
          cd_dummy01: row.cd_dummy01,
          cd_item: row.cd_item,
          qt: 0,
          __row_order: index,
        };
        sizeSlotColumns.forEach((slot) => {
          pivotRow[slot] = 0;
        });
        groupedRows.set(rowKey, pivotRow);
      }
      const slot = sizeToSlot.get(`${row.cd_var3 ?? ""}`.trim());
      if (slot) {
        pivotRow[slot] = Number(pivotRow[slot] ?? 0) + Number(row.qt ?? 0);
        pivotRow.qt += Number(row.qt ?? 0);
      }
    });
    // 4) 피벗 rowData 갱신: 탭 전환 없이도 즉시 반영되도록 상태를 교체한다.
    setDataSources((prev: any) => ({
      ...prev,
      grid2: getPivotRowsInSourceOrder(Array.from(groupedRows.values())),
    }));
    if (selectedRow) {
      const no_wo = selectedRow.no_wo;

      gridRefs.current["grid3"].current.applyFilters({ no_wo: no_wo });
      gridRefs.current["grid3"].current.selectAndFocusRow(0);
      gridRefs.current["grid4"].current.applyFilters({ no_wo: no_wo });
      gridRefs.current["grid4"].current.selectAndFocusRow(0);
      gridRefs.current["grid2"].current.applyFilters({ no_wo: no_wo });
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
      setSelectedDocu(no_wo);

      columns.grid2.map((row: any) => {
        if (row.cd_column && sizeSlotColumns.includes(row.cd_column)) {
          const slotLabel = slotLabelByColumn.get(row.cd_column);
          const data = {
            cd_column: row.cd_column,
            nm_column: slotLabel ?? row.nm_column,
            fg_visible: slotLabel ? "Y" : "N",
          };
          virtual.push(data);
        }
      });
      setTimeout(() => {
        gridRefs.current["grid2"].current.changeColumnConfigs(virtual);
      }, 0);
      // cd_var3 매핑
      sizeToSlotRef.current = sizeToSlot;
      _nm_rout_old.current = selectedRow.cd_rout;
      _no_style_old.current = selectedRow.no_style;
    } else {
      return;
    }
  };

  const onCellValueChanged = async (event: CellValueChangedEvent) => {
    if (!event) return;
    let dt: any, dt2: any;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    if (isRestoring.current) {
      // 되돌리는 중이면 이벤트 무시
      isRestoring.current = false;
      return;
    }
    if (colId === "no_wo") {
      const cnt1 = gridRefs.current["grid2"].current?.getDisplayedRowCount();
      const cnt2 = gridRefs.current["grid3"].current?.getDisplayedRowCount();
      if (cnt1 > 0 || cnt2 > 0) {
        const confirm = await showDialog(
          "confirm",
          "지시번호를 변경하시면, 처음부터 값을 다시 입력하셔야 합니다. \n변경하시겠습니까?"
        );
        if (!confirm) {
          isRestoring.current = true;
          rowNode.setDataValue(colId, event.oldValue);
          gridRefs.current["grid1"].current?.resetUpdate(true);
          gridRefs.current["grid2"].current?.resetUpdate(true);
          gridRefs.current["grid3"].current?.resetUpdate(true);
          gridRefs.current["grid4"].current.resetUpdate(true);

          setTimeout(() => {
            gridRefs.current["grid1"].current.setFocusedCell(
              event.rowIndex,
              colId
            );
          }, 0);

          return;
        }
        gridRefs.current["grid3"].current?.deleteAllRows();
        gridRefs.current["grid2"].current?.deleteAllRows();
        gridRefs.current["grid4"].current?.deleteAllRows();
      }
    } else if (colId === "no_style") {
      const sqlQuery1 = {
        /*sql: `select pr2.*,yn_autorel, case when coalesce(yn_autorel,'N') = 'Y' then 'R' else 'F' END AS st_wo
        from prd_bomh_tex bom
        left outer join prd_rout pr2 on pr2.cd_firm = bom.cd_firm and pr2.cd_rout = bom.cd_rout
        left outer join mas_wotype mw on pr2.cd_firm = mw.cd_firm and pr2.cd_wo = mw.cd_wo
        where bom.cd_firm = :cd_firm and left(bom.no_style,:length) = :no_style
        limit 1`,*/
        sql: ` select  pr2.*,yn_autorel, case when coalesce(yn_autorel,'N') = 'Y' then 'R' else 'F' END AS st_wo
        from  prd_rout pr2
        left outer join mas_wotype mw on pr2.cd_firm = mw.cd_firm and pr2.cd_wo = mw.cd_wo
        where   pr2.cd_firm = :cd_firm and pr2.cd_rout = 'T20'
        limit 1`,
        cd_firm: userInfo.cd_firm,
        //length: updatedRow.no_style.toString().length,
        //no_style: updatedRow.no_style,
      };
      dt2 = await getTablebySql(sqlQuery1);
      if (dt2.length > 0) {
        rowNode.setDataValue("cd_rout", dt2[0].cd_rout);
        rowNode.setDataValue("nm_rout", dt2[0].nm_rout);
        rowNode.setDataValue("cd_wo", dt2[0].cd_wo);
        rowNode.setDataValue("st_wo", dt2[0].st_wo);
        rowNode.setDataValue("yn_autorel", dt2[0].yn_autorel);
        rowNode.setDataValue("cd_wh_rcv", dt2[0].cd_wh_good);
        rowNode.setDataValue("cd_wh_isu", dt2[0].cd_wh_isu);
      }
    } else if (colId === "cd_rout") {
      // if (_nm_rout_old.current !== updatedRow.cd_rout) {
      //   const sqlQuery1 = {
      //     sql: "select coalesce(r.cd_wo,'') as cd_wo, coalesce(r.cd_wh_isu,'') as cd_wh_isu, coalesce(wh.nm_wh,'') as nm_wh from prd_rout r left outer join mas_wh wh on r.cd_firm = wh.cd_firm and r.cd_wh_isu = wh.cd_wh where r.cd_firm = :cd_firm and r.cd_rout = :cd_rout",
      //     cd_firm: userInfo.cd_firm,
      //     cd_rout: updatedRow.cd_rout,
      //   };
      //   dt = await getTablebySql(sqlQuery1);
      //   if (dt != null && dt.length > 0) {
      //     event.node.setDataValue("cd_wo", dt[0].cd_wo);
      //     event.node.setDataValue("cd_wh_isu", dt[0].cd_wh_isu);
      //     event.node.setDataValue("nm_wh_isu", dt[0].nm_wh);
      //   }
      // }
      // _nm_rout_old.current = updatedRow.cd_rout;
    } else if (colId === "qt_wo") {
      gridRefs.current["grid4"].current.forEachFilteredNode((node: RowNode) => {
        node.setDataValue("qt_need", node.data.qt_unit * rowData.qt_wo);
      });
      gridRefs.current["grid3"].current.forEachFilteredNode((node: RowNode) => {
        node.setDataValue("qt_wo", rowData.qt_wo);
      });
    } else if (colId === "dt_wo") {
      rowData.dt_start = rowData.dt_wo;
      if (parseInt(rowData.dt_wo, 10) > parseInt(rowData.dt_end, 10)) {
        rowData.dt_end = rowData.dt_wo;
      }
    } else if (colId === "dt_start") {
      if (rowData.dt_end === "") {
        rowData.dt_end = rowData.dt_start;
      } else if (
        parseInt(rowData.dt_start, 10) > parseInt(rowData.dt_end, 10)
      ) {
        await showDialog("alter", "시작일이 종료일보다 이후입니다.");
        rowData.dt_start = rowData.dt_end;
      }
    } else if (colId === "dt_end") {
      if (rowData.dt_start === "") {
        rowData.dt_start = rowData.dt_end;
      } else if (
        parseInt(rowData.dt_end, 10) < parseInt(rowData.dt_start, 10)
      ) {
        await showDialog("alter", "종료일이 시작일보다 이전입니다.");
        rowData.dt_end = rowData.dt_start;
      }
    } else if (colId === "cd_wo") {
      const sqlQuery1 = {
        sql: `select yn_autorel,yn_autorcv, case when coalesce(yn_autorel,'N') = 'Y' then 'R' else 'F' END AS st_wo from mas_wotype where cd_firm = :cd_firm and cd_wo = :cd_wo`,
        cd_firm: userInfo.cd_firm,
        cd_wo: rowData.cd_wo,
      };
      let data = await getTablebySql(sqlQuery1);
      rowNode.setDataValue("yn_autorcv", data[0].yn_autorcv);
      rowNode.setDataValue("yn_autorel", data[0].yn_autorel);
      rowNode.setDataValue("st_wo", data[0].st_wo);
    }
  };

  const onCellValueChanged2 = async (event: any) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "yn_end") {
      if (!isYnEnd.current) return;
      if (rowData.yn_end === "Y") {
        gridRefs.current["grid3"].current.forEachFilteredNode(
          (node: RowNode) => {
            if (!node?.data) return;
            if (rowData.KeyField === node.data?.KeyField) return;
            node.setDataValue("qt_wo", rowData.qt_wo);
          }
        );
      } else if (rowData.yn_end === "N") {
        rowData.yn_end = "Y";
      }
      isYnEnd.current = false;
    }
  };

  const onCellValueChanged3 = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "qt_unit") {
      rowData.qt_need =
        rowData.qt_unit *
        gridRefs.current["grid1"].current.getSelectedRows()[0].qt_wo;
    }
    if (colId === "cd_matl") {
      if (
        rowData.cd_matl === "" ||
        rowData.cd_matl === null ||
        rowData.cd_matl === undefined
      )
        return;
      const sqlQuery2 = {
        sql: "select coalesce(mi.cd_wh_isu,'')as cd_wh_isu, coalesce(mw.nm_wh,'') as nm_wh_isu from mas_item mi left outer join mas_wh mw on mi.cd_firm = mw.cd_firm and mi.cd_wh_isu = mw.cd_wh where mi.cd_firm = :cd_firm and mi.cd_item = :cd_item",
        cd_firm: userInfo.cd_firm,
        cd_item: rowData.cd_matl,
      };
      const params2 = await getTablebySql(sqlQuery2);
      let cdWhIsu = params2[0]?.cd_wh_isu ?? "";
      let nmWhIsu = params2[0]?.nm_wh_isu ?? "";
      rowData.cd_wh = cdWhIsu;
      rowData.nm_wh = nmWhIsu;
    }
  };

  const onCellValueChanged4 = async (event: any) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const focusedNodelH = gridRefs.current["grid1"].current.focusedRowNode();
    if (
      colId === "qt_wo1" ||
      colId === "qt_wo2" ||
      colId === "qt_wo3" ||
      colId === "qt_wo4" ||
      colId === "qt_wo5" ||
      colId === "qt_wo6" ||
      colId === "qt_wo7" ||
      colId === "qt_wo8" ||
      colId === "qt_wo9" ||
      colId === "qt_wo10" ||
      colId === "qt_wo11" ||
      colId === "qt_wo12" ||
      colId === "qt_wo13" ||
      colId === "qt_wo14" ||
      colId === "qt_wo15" ||
      colId === "qt_wo16" ||
      colId === "qt_wo17" ||
      colId === "qt_wo18" ||
      colId === "qt_wo19" ||
      colId === "qt_wo20"
    ) {
      let qt = 0;
      for (let i = 1; i <= 20; i++) {
        qt += Number(rowData[`qt_wo${i}`] || 0);
      }
      rowNode.setDataValue("qt", qt);
      await Promise.resolve();
      const footerData = gridRefs.current["grid2"].current.getFooterData();

      focusedNodelH.setDataValue("qt_wo", footerData.qt);
    }
  };

  const onSelectionChanged = (event: any) => {
    isYnEnd.current = true;
  };

  const TabChangedEvent = async (index: any) => {
    setSelectedTabPage(index);
  };
  const validateDetailGrids = async () => {
    const grid2 = gridRefs.current["grid2"].current;
    const grid3 = gridRefs.current["grid3"].current;
    const grid4 = gridRefs.current["grid4"].current;

    // 👉 row 개수 확인
    const grid2Count = grid2?.getDisplayedRowCount?.() ?? 0;
    const grid3Count = grid3?.getDisplayedRowCount?.() ?? 0;
    const grid4Count = grid4?.getDisplayedRowCount?.() ?? 0;

    if (grid2Count === 0 || grid3Count === 0 || grid4Count === 0) {
      await showDialog(
        "alert",
        "전개되지 않은 하단 탭이 존재합니다. 전개 후 저장해 주세요"
      );
      return false;
    }

    return true;
  };
  const onRowDataReady = () => {
    const focusKey = focusKeyRef.current;
    if (!focusKey) return;

    const grid = gridRefs.current["grid1"].current;

    const rowCount = grid.getDisplayedRowCount();
    let targetIndex = -1;

    for (let i = 0; i < rowCount; i++) {
      const rowNode = grid.getRowNodeAtIndex(i);
      if (!rowNode?.data) continue;

      if (rowNode.data.no_wo === focusKey.no_wo) {
        targetIndex = i;
        break;
      }
    }

    if (targetIndex >= 0) {
      grid.selectAndFocusRow(targetIndex);
      grid.scrollToIndex?.(targetIndex);
    } else {
      grid.selectAndFocusRow(0);
    }

    focusKeyRef.current = null;
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChanged,
          onCellValueChanged: onCellValueChanged,
          onRowDataReady: onRowDataReady,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChanged4,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onCellValueChanged: onCellValueChanged2,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onCellValueChanged: onCellValueChanged3,
          onSelectionChanged: onSelectionChanged,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        />
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            events={grids.grid1.events}
            etcProps={{
              rowSelection: "single",
              title: "작업지시",
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnTab
            selectedTab={selectedTabPage}
            titles={["사양정보", "공정정보", "자재정보"]}
            width={"100%"}
            onTabChanged={TabChangedEvent}
            header={[
              <SnTabHeader
                body={
                  <div>
                    <SnButton
                      text={"전개"}
                      onClick={() => {
                        onBtnsClick("btnReload");
                      }}
                    />
                  </div>
                }
                onBtnAddClick={onBtnsClick}
                onBtnDeleteClick={onBtnsClick}
              />,

              <SnTabHeader
                body={
                  <div className="relative flex space-x-2">
                    <SnButton
                      text={"전개"}
                      onClick={() => {
                        onBtnsClick("btnReload");
                      }}
                    />
                  </div>
                }
                onBtnAddClick={onBtnsClick}
                onBtnDeleteClick={onBtnsClick}
              />,
              <SnTabHeader
                body={
                  <div className="relative flex space-x-2">
                    <SnButton
                      text={"전개"}
                      onClick={() => {
                        onBtnsClick("btnReload");
                      }}
                    />
                  </div>
                }
                onBtnAddClick={onBtnsClick}
                onBtnDeleteClick={onBtnsClick}
              />,
            ]}
            body={[
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={grids.grid2.columns}
                rowData={grids.grid2.dataSource}
                events={grids.grid2.events}
                etcProps={{
                  rowSelection: "single",
                }}
              />,
              <SnGrid
                ref={gridRefs.current["grid3"]}
                columnConfig={grids.grid3.columns}
                rowData={grids.grid3.dataSource}
                events={grids.grid3.events}
                etcProps={{
                  rowSelection: "single",
                }}
              />,

              <SnGrid
                ref={gridRefs.current["grid4"]}
                columnConfig={grids.grid4.columns}
                rowData={grids.grid4.dataSource}
                events={grids.grid4.events}
                etcProps={{
                  rowSelection: "single",
                }}
              />,
            ]}
          />
        </SnResizeBar>
        <SnFileUploadBox
          selectedDocu={selectedDocu}
          selectedLineDocu={selectedLineDocu}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_PRD_WMO_REG_001_TEX;
